import {
  WOConstraintsModal,
  WOMultipleOrdersModal,
  WODependentServiceModal,
} from '@pinnacleautomotive/clarity-components';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { NewOrderTypes } from '../../../common/enums/enums';
import DiscardedOrderConfirmation from './components/DiscardedOrderConfirmation';
import NewOrderCreatedSuccessfully from './components/NewOrderCreatedSuccessfully';
import NewOrderDuplicatedError from './components/NewOrderDuplicatedError';
import NewOrderErrorCreating from './components/NewOrderErrorCreating';
import { getSelectorActionsBasedOnType } from './helper';
import SinglePage from './SinglePage';
import Stepper from './Stepper';

const SINGLE_PAGE_KEY = 'SINGLE_PAGE_ORDER_MODE';

export default function NewOrder({ orderType = NewOrderTypes.SIMPLE_ORDER }) {
  const dispatch = useDispatch();
  const [openMultipleOrders, setOpenMultipleOrders] = useState(false);
  const [singlePage, setSinglePage] = useState(
    sessionStorage.getItem(SINGLE_PAGE_KEY) === 'true'
  );
  const {
    clearErrorAction,
    clearOrderAction,
    discardOrderAction,
    saveOrderAction,
    dataToSaveSelector,
    discardedSelector,
    saveErrorSelector,
    selectWorkOrderSavedSelector,
    isLoadingSelector,
    selectedCategoriesSelector,
    selectedPricesWithDetailsSelector,
  } = getSelectorActionsBasedOnType(orderType);
  const workOrderSaved = useSelector(selectWorkOrderSavedSelector);
  const saveError = useSelector(saveErrorSelector);
  const discarded = useSelector(discardedSelector);
  const dataToSave = useSelector(dataToSaveSelector);
  const isLoading = useSelector(isLoadingSelector);
  const [currentStep, setCurrentStep] = useState(null);
  const history = useHistory();
  const selectedCategories = useSelector(selectedCategoriesSelector);
  const selectedPrices = useSelector(selectedPricesWithDetailsSelector) || [];

  const goToServicesStep = () => {
    setCurrentStep(null);
    setTimeout(() => setCurrentStep(1), 0);
    document.getElementById('selectDepartments')?.focus();
    dispatch(clearErrorAction());
    setOpenMultipleOrders(false);
  };

  const goToDiscardOrder = () => {
    dispatch(discardOrderAction());
    setOpenMultipleOrders(false);
  };

  const goToWorkOrder = () => {
    dispatch(clearOrderAction());
    history.goBack();
    setOpenMultipleOrders(false);
  };

  const sendToSaveTheNewOrder = () => {
    dispatch(saveOrderAction(dataToSave));
  };

  const toggleMode = () => {
    sessionStorage.setItem(SINGLE_PAGE_KEY, !singlePage);
    setSinglePage(!singlePage);
  };

  const isMultipleOrders = () =>
    selectedCategories?.length > 1
      ? setOpenMultipleOrders(true)
      : sendToSaveTheNewOrder();

  const editServices = () => {
    goToServicesStep();
    setOpenMultipleOrders(false);
  };

  const excludeServiceCreateWO = () => {
    dispatch(
      saveOrderAction({ ...dataToSave, excludeServiceLimitOrders: true })
    );
  };

  const excludeDependentServiceCreateWO = () => {
    dispatch(saveOrderAction({ ...dataToSave, excludeOrderDependency: true }));
  };

  return (
    <>
      <NewOrderCreatedSuccessfully
        open={!!workOrderSaved}
        onClose={goToWorkOrder}
        vendorOrder={workOrderSaved}
      />
      <NewOrderErrorCreating
        error={saveError}
        onClose={goToWorkOrder}
        onConfirm={sendToSaveTheNewOrder}
      />
      <NewOrderDuplicatedError
        error={saveError}
        onDiscard={goToDiscardOrder}
        onEditOrder={goToServicesStep}
      />
      <WOConstraintsModal
        error={saveError}
        onDiscard={goToDiscardOrder}
        onEditServices={goToServicesStep}
        onExcludeServiceAndCreate={excludeServiceCreateWO}
        selectedPrices={selectedPrices}
      />
      <WODependentServiceModal
        error={saveError}
        selectedPrices={selectedPrices}
        onCreateOrder={excludeDependentServiceCreateWO}
        onEditServices={goToServicesStep}
        onDiscard={goToDiscardOrder}
      />
      <WOMultipleOrdersModal
        open={openMultipleOrders}
        onClose={() => setOpenMultipleOrders(false)}
        editServices={editServices}
        createOrders={sendToSaveTheNewOrder}
        isLoading={isLoading}
        numberOfOrders={selectedCategories?.length}
      />
      <DiscardedOrderConfirmation open={discarded} onClose={goToWorkOrder} />
      {(singlePage && (
        <SinglePage
          onToggleMode={toggleMode}
          onSave={isMultipleOrders}
          orderType={orderType}
        />
      )) || (
        <Stepper
          onToggleMode={toggleMode}
          currentStep={currentStep}
          onSave={isMultipleOrders}
          orderType={orderType}
        />
      )}
    </>
  );
}
